/* eslint-disable react-hooks/exhaustive-deps */
import { memo, useEffect, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'swiper/modules'

import { useIsMounted } from '@/hooks'
import { composeClasses } from '@/utils/compose-classes'
import {
  SlidePrevButton,
  SlideNextButton,
  DotsComponent,
  ImageContainer
} from '../components'
import './custom.css'

export interface CarouselProps {
  photos: string[]
  classNameCarousel?: string
  classNameImage?: string
  withDots?: boolean
  rounded?: string
  withOverLay?: boolean
  withDragGestures?: boolean
  onChangeIndex?: (index: number) => void
  classNameContainerImage?: string
  width?: number
  classNameContainerSwiper?: string
  classNameSwiper?: string
  withAltDots?: boolean
}

/**
 * @param onChangeIndex callback when the index of the carousel changes
 * @param width width of the images to be displayed, this is used to improve performance
 */
export const Carousel = memo(
  ({
    photos = [],
    classNameCarousel,
    classNameImage,
    withDots,
    classNameContainerSwiper,
    classNameContainerImage,
    classNameSwiper,
    withOverLay,
    withDragGestures,
    onChangeIndex,
    rounded,
    width,
    withAltDots
  }: CarouselProps) => {
    const [indexActive, setIndexActive] = useState<number>(0)

    const isMounted = useIsMounted()

    useEffect(() => {
      if (!isMounted) {
        return
      }

      onChangeIndex?.(indexActive)
    }, [indexActive])

    return (
      <div
        data-testid="container-carousel"
        className={composeClasses(
          classNameCarousel,
          'relative z-0 overflow-hidden h-full w-full bg-monopolio-grey-05'
        )}
      >
        <Swiper
          loop
          allowTouchMove={Boolean(withDragGestures)}
          spaceBetween={0}
          edgeSwipeThreshold={10}
          longSwipesRatio={0.1}
          className={composeClasses(
            rounded,
            classNameContainerSwiper,
            'object-cover w-full h-full'
          )}
          slidesPerView={1}
          touchEventsTarget="container"
          modules={withAltDots ? [Pagination] : []}
          pagination={
            withAltDots
              ? {
                  dynamicBullets: true,
                  bulletClass: 'h-2.5 w-2.5 bg-white swiper-pagination-bullet'
                }
              : {}
          }
        >
          {photos?.map((photo, index) => {
            const url = width && width > 0 ? `${photo}?w=${width}` : photo

            return (
              <SwiperSlide
                key={photo}
                className={composeClasses(classNameSwiper, 'relative')}
              >
                <ImageContainer
                  rounded={rounded}
                  classNameContainerImage={classNameContainerImage}
                  classNameImage={classNameImage}
                  setIndexActive={setIndexActive}
                  photo={url}
                  withOverLay={withOverLay}
                  index={index}
                />
              </SwiperSlide>
            )
          })}

          {photos?.length > 1 && <SlidePrevButton />}
          {photos?.length > 1 && <SlideNextButton />}

          {withDots && (
            <DotsComponent photos={photos} indexActive={indexActive} />
          )}
        </Swiper>
      </div>
    )
  }
)

export default Carousel
